import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Body, Muted } from "../../components/Typography";
import { ApiError, api } from "../../lib/api";
import type {
  BillingSummaryResponse,
  CouponPreviewResponse,
  DeviceOrderResponse,
  DeviceReturnResponse,
  InvoiceResponse,
  PaymentMethodResponse,
  PrepayPreviewResponse,
  StoreResponse,
  SubscriptionResponse,
} from "../../lib/api";
import { DEVICE_NAME, dollars } from "../Shopping/Shopping";
import styles from "./BillingPanel.module.css";

// PRD PAYWALL §6: the Store Settings → Billing tab. Money-moving controls are
// admin-level (owner = admin, per §1); everyone else gets the read-only status card —
// mirroring how the Members panel gates management.

const PLAN_NAMES: Record<string, string> = {
  starter: "Starter",
  standard: "Standard",
  pro: "Pro",
  custom: "Custom",
};

// PRD §1 (amended 2026-10-02): flat price covering the plan's cameras, overage beyond.
const planLine = (summary: BillingSummaryResponse): string => {
  const plan = summary.plan;
  if (!plan) return "";
  // PRD PLANS_CATALOG §1 (price lock): the store's own locked terms — or its Custom quote —
  // win over the plan row; the row applies only to a store never activated.
  const sub = summary.subscription;
  const locked = sub?.terms_locked_at != null;
  const priceCents = (locked ? sub?.monthly_price_cents : null) ?? plan.monthly_price_cents;
  const included = (locked ? sub?.included_cameras : null) ?? plan.included_cameras;
  const overageCents =
    (locked ? sub?.overage_cents_per_camera : null) ?? plan.overage_cents_per_camera;
  const extra = Math.max(0, summary.billable_cameras - included);
  const base = `${dollars(priceCents)}/mo`;
  if (plan.code === "custom" && !locked) {
    return ` — ${dollars(overageCents)}/camera/mo × ${summary.billable_cameras}`;
  }
  if (extra === 0) {
    return ` — ${base}, ${summary.billable_cameras} of ${included} cameras in use`;
  }
  return ` — ${base} + ${extra} extra camera${extra === 1 ? "" : "s"} × ${dollars(overageCents)}`;
};

const fmtDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

const errorText = (e: unknown, fallback: string): string =>
  e instanceof ApiError ? (e.body?.error ?? e.message) : fallback;

export const statusCopy = (sub: SubscriptionResponse): string => {
  switch (sub.status) {
    case "pending_billing":
      return "Awaiting billing — installed, not started";
    case "trialing":
      return sub.canceled_at ? "Canceling" : "Free trial";
    case "active":
      return sub.canceled_at ? "Active — cancellation scheduled" : "Active";
    case "paused":
      return sub.paused_reason ? `Paused — ${sub.paused_reason.replaceAll("_", " ")}` : "Paused";
    case "canceled":
      return "Canceled";
    case "complimentary":
      return "Complimentary — pilot service, never billed";
    default:
      return sub.status;
  }
};

const invoiceStatusLabel = (invoice: InvoiceResponse): string =>
  invoice.status === "draft" ? "preview — dry run" : invoice.status;

type BillingPanelProps = {
  storeId: string;
  store: StoreResponse | null;
  currentUserId: string | undefined;
};

export const BillingPanel = ({ storeId, store, currentUserId }: BillingPanelProps) => {
  const [summary, setSummary] = useState<BillingSummaryResponse | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [methods, setMethods] = useState<PaymentMethodResponse[]>([]);
  const [invoices, setInvoices] = useState<InvoiceResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [couponPreview, setCouponPreview] = useState<CouponPreviewResponse | null>(null);
  const [prepayPreview, setPrepayPreview] = useState<(PrepayPreviewResponse & { term: 12 }) | null>(
    null,
  );
  const [copied, setCopied] = useState(false);
  const [depositOrders, setDepositOrders] = useState<DeviceOrderResponse[]>([]);
  const [returnInfo, setReturnInfo] = useState<DeviceReturnResponse | null>(null);

  const load = useCallback(async () => {
    try {
      const [fetched, members] = await Promise.all([
        api.getBillingSummary(storeId),
        api.listMembers(storeId),
      ]);
      const admin =
        store?.owner_id === currentUserId ||
        ["owner", "admin"].includes(members.find((m) => m.user_id === currentUserId)?.role ?? "");
      setSummary(fetched);
      setCanManage(admin);
      if (admin) {
        const [wallet, history] = await Promise.all([
          api.listPaymentMethods(storeId),
          api.listInvoices(storeId),
        ]);
        setMethods(wallet);
        setInvoices(history);
        // A deposit box that is still out shapes the cancel copy and the return notice
        // (PRD §1, 2026-10-03). Best-effort: the panel works without it.
        try {
          const orders = await api.listDeviceOrders(storeId);
          const open = orders.filter((o) => o.deposit && !o.refunded_at && o.status !== "returned");
          setDepositOrders(open);
          if (open.length > 0) setReturnInfo(await api.getDeviceReturn());
        } catch {
          setDepositOrders([]);
        }
      }
    } catch (e) {
      setError(errorText(e, "Couldn't load billing."));
    } finally {
      setLoading(false);
    }
  }, [storeId, store?.owner_id, currentUserId]);

  useEffect(() => {
    void load();
  }, [load]);

  const sub = summary?.subscription ?? null;

  const nextCharge = useMemo((): { date: string | null; amount: string } | null => {
    if (!sub || !summary) return null;
    if (sub.status === "trialing" && !sub.canceled_at) {
      return {
        date: sub.trial_ends_at,
        amount: summary.next_charge ? dollars(summary.next_charge.total_cents) : "—",
      };
    }
    if (sub.status === "active" && !sub.canceled_at) {
      return {
        date: sub.period_end,
        amount: summary.next_charge ? dollars(summary.next_charge.total_cents) : "—",
      };
    }
    return null;
  }, [sub, summary]);

  const run = async (fallback: string, fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(errorText(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () =>
    run("Couldn't cancel the subscription.", async () => {
      await api.cancelSubscription(storeId);
      setCancelOpen(false);
      await load();
    });

  const handleCouponCheck = async (): Promise<void> => {
    const code = coupon.trim();
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      setCouponPreview(await api.previewCoupon(storeId, { coupon_code: code }));
    } catch (e) {
      setError(errorText(e, "Couldn't check that coupon."));
    } finally {
      setBusy(false);
    }
  };

  const handleCouponApply = () =>
    run("Couldn't apply the coupon.", async () => {
      if (!couponPreview) return;
      await api.applyCoupon(storeId, { coupon_code: couponPreview.code });
      setCoupon("");
      setCouponPreview(null);
      setNotice("Coupon applied.");
    });

  const handlePrepayPick = async (term: 12): Promise<void> => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const preview = await api.previewPrepay(storeId, { term });
      setPrepayPreview({ ...preview, term });
    } catch (e) {
      setError(errorText(e, "Couldn't price the prepay term."));
    } finally {
      setBusy(false);
    }
  };

  const handlePrepayConfirm = () =>
    run("Couldn't complete the prepay purchase.", async () => {
      if (!prepayPreview) return;
      const result = await api.purchasePrepay(storeId, { term: prepayPreview.term });
      setPrepayPreview(null);
      setNotice(
        result.dry_run
          ? "Charging isn't live yet — a preview invoice was recorded; nothing was charged and your term is unchanged."
          : `Prepaid ${prepayPreview.term} months — ${dollars(result.invoice.total_cents)} charged.`,
      );
    });

  const handleRemove = (methodId: string) =>
    run("Couldn't remove that card.", async () => {
      await api.removePaymentMethod(storeId, methodId);
      setRemoveTarget(null);
    });

  const handleCopyCode = async (): Promise<void> => {
    if (!sub?.sales_code) return;
    try {
      await navigator.clipboard.writeText(sub.sales_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — the code is on screen either way.
    }
  };

  if (loading) return <div className={styles.status}>Loading…</div>;

  if (!sub) {
    return (
      <div className={styles.panel}>
        {error && (
          <div role="alert" className={styles.error}>
            {error}
          </div>
        )}
        <Body>
          <Muted>
            {summary?.predates_billing
              ? "This store predates billing — service continues as before; activate a plan from the setup wizard's billing step whenever you're ready."
              : "No billing record yet."}
          </Muted>
        </Body>
      </div>
    );
  }

  const openDeposit = depositOrders[0] ?? null;
  const dueDeposit =
    depositOrders
      .filter((o): o is DeviceOrderResponse & { return_due_at: string } => o.return_due_at !== null)
      .at(0) ?? null;
  const canCancel =
    canManage &&
    !sub.canceled_at &&
    ["pending_billing", "trialing", "active", "paused"].includes(sub.status);
  const canPrepay =
    canManage &&
    sub.status === "active" &&
    sub.prepay_term === 1 &&
    !sub.canceled_at &&
    (summary?.plan?.prepayable ?? true);
  // The dollar figure next to the percentage: people feel $210 more than 10%. The row's
  // percentage, never a literal; the year packaged in whole dollars as the engine charges.
  const prepayPercent = summary?.plan?.prepay_12_pct ?? 0;
  const prepaySavings = (): string => {
    const monthly = summary?.next_charge?.base_cents;
    if (!monthly) return "";
    const base = monthly * 12;
    const year = Math.floor((base - Math.floor((base * prepayPercent) / 100)) / 100) * 100;
    return ` (${dollars(base - year)})`;
  };
  const showCoupon = canManage && ["trialing", "active"].includes(sub.status) && !sub.canceled_at;

  return (
    <div className={styles.panel}>
      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
      {notice && <output className={styles.notice}>{notice}</output>}

      <section className={styles.card}>
        <h3 className={styles.cardTitle}>Plan</h3>
        <dl className={styles.facts}>
          <div>
            <dt>Plan</dt>
            <dd>
              {summary?.plan?.name ?? PLAN_NAMES[sub.plan_code] ?? sub.plan_code}
              {summary ? planLine(summary) : ""}
            </dd>
          </div>
          <div>
            <dt>Status</dt>
            <dd>{statusCopy(sub)}</dd>
          </div>
          {sub.status === "trialing" && sub.trial_ends_at && !sub.canceled_at && (
            <div>
              <dt>Trial ends</dt>
              <dd>{fmtDate(sub.trial_ends_at)}</dd>
            </div>
          )}
          {sub.canceled_at && sub.status !== "canceled" && sub.period_end && (
            <div>
              <dt>Service until</dt>
              <dd>{fmtDate(sub.period_end)}</dd>
            </div>
          )}
          <div>
            <dt>Next charge</dt>
            <dd>{nextCharge?.date ? `${fmtDate(nextCharge.date)} · ${nextCharge.amount}` : "—"}</dd>
          </div>
          {sub.coupon_code && (
            <div>
              <dt>Coupon</dt>
              <dd>{sub.coupon_code}</dd>
            </div>
          )}
          {sub.sales_code && (
            <div>
              <dt>Sales Code</dt>
              <dd className={styles.salesCode}>
                <code>{sub.sales_code}</code>
                <button
                  type="button"
                  className={styles.textBtn}
                  onClick={() => void handleCopyCode()}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </dd>
            </div>
          )}
        </dl>
        {!canManage && <p className={styles.hint}>Billing is managed by the store's admins.</p>}
      </section>

      {canManage && (
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Payment cards</h3>
          {methods.length === 0 && <p className={styles.hint}>No card on file.</p>}
          <ul className={styles.list}>
            {methods.map((m) => (
              <li key={m.id} className={styles.row}>
                <span className={styles.cardName}>
                  {(m.brand ?? m.kind).toUpperCase()} {m.last4 ? `•••• ${m.last4}` : m.stripe_pm_id}
                  {m.exp_month && m.exp_year ? ` · ${m.exp_month}/${m.exp_year}` : ""}
                </span>
                <span className={styles.rowTrail}>
                  {m.is_primary && <span className={styles.primaryBadge}>Primary</span>}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setRemoveTarget(m.id)}
                  >
                    Remove
                  </Button>
                </span>
                {removeTarget === m.id && (
                  <div className={styles.confirm}>
                    <span className={styles.confirmText}>
                      {methods.length === 1
                        ? "This is the store's only card. If money is owed when the wallet empties, service pauses immediately."
                        : m.is_primary
                          ? "This is the primary card — the next oldest card becomes primary."
                          : "Remove this card?"}
                    </span>
                    <Button size="sm" disabled={busy} onClick={() => void handleRemove(m.id)}>
                      Remove card
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy}
                      onClick={() => setRemoveTarget(null)}
                    >
                      Keep it
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {addingCard ? (
            <AddCardFlow
              storeId={storeId}
              onDone={() => {
                setAddingCard(false);
                void load();
              }}
              onCancel={() => setAddingCard(false)}
            />
          ) : (
            <div>
              <Button disabled={busy} onClick={() => setAddingCard(true)}>
                Add card
              </Button>
            </div>
          )}
        </section>
      )}

      {sub.status === "canceled" && dueDeposit && returnInfo && (
        <section className={styles.card} aria-label="Return your box">
          <h3 className={styles.cardTitle}>Return your box for the deposit</h3>
          <Body>
            Ship the {DEVICE_NAME} back by <strong>{fmtDate(dueDeposit.return_due_at)}</strong> and
            we refund the {dollars(dueDeposit.price_cents)} deposit to your card within 5 to 10
            business days of receiving it. Return shipping is yours; the original shipping is not
            refunded.
          </Body>
          <address className={styles.returnAddress}>
            {returnInfo.address_lines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </address>
          {returnInfo.note && (
            <Body>
              <Muted>{returnInfo.note}</Muted>
            </Body>
          )}
          <Body>
            <Muted>Write "{store?.name ?? "your store"}" on a note inside the package.</Muted>
          </Body>
        </section>
      )}

      {canManage && (canPrepay || showCoupon || canCancel) && (
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Plan changes</h3>

          {canPrepay && (
            <div className={styles.block}>
              <span className={styles.blockLabel}>Prepay and save</span>
              <div className={styles.btnRow}>
                <Button variant="outline" disabled={busy} onClick={() => void handlePrepayPick(12)}>
                  12 months — save {prepayPercent}%{prepaySavings()}
                </Button>
              </div>
              {prepayPreview && (
                <div className={styles.confirm}>
                  <span className={styles.confirmText}>
                    Prepay {prepayPreview.term} months for{" "}
                    <strong>{dollars(prepayPreview.charge.total_cents)}</strong>, covering{" "}
                    {fmtDate(prepayPreview.period_start)} to {fmtDate(prepayPreview.period_end)}.
                    One up-front charge; prepaid terms are final.
                  </span>
                  <Button size="sm" disabled={busy} onClick={() => void handlePrepayConfirm()}>
                    Confirm prepay
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setPrepayPreview(null)}
                  >
                    Back
                  </Button>
                </div>
              )}
            </div>
          )}

          {showCoupon && (
            <div className={styles.block}>
              <label className={styles.blockLabel} htmlFor="billing-tab-coupon">
                Coupon code
              </label>
              <div className={styles.btnRow}>
                <Input
                  id="billing-tab-coupon"
                  value={coupon}
                  onChange={(e) => {
                    setCoupon(e.target.value);
                    setCouponPreview(null);
                  }}
                  disabled={busy}
                  placeholder="Enter a code"
                />
                <Button
                  variant="outline"
                  disabled={busy || !coupon.trim()}
                  onClick={() => void handleCouponCheck()}
                >
                  Check code
                </Button>
              </div>
              {couponPreview && (
                <div className={styles.confirm}>
                  <span className={styles.confirmText}>
                    {couponPreview.code} — {couponPreview.percent}% off for{" "}
                    {couponPreview.duration_months} month
                    {couponPreview.duration_months === 1 ? "" : "s"}.
                    {couponPreview.replaces_coupon_code &&
                      ` This replaces your current coupon ${couponPreview.replaces_coupon_code} — its remaining discount is lost.`}
                  </span>
                  <Button size="sm" disabled={busy} onClick={() => void handleCouponApply()}>
                    Apply coupon
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setCouponPreview(null)}
                  >
                    Never mind
                  </Button>
                </div>
              )}
            </div>
          )}

          {canCancel && (
            <div className={styles.block}>
              <span className={styles.blockLabel}>Cancel subscription</span>
              {cancelOpen ? (
                <div className={styles.confirm}>
                  <span className={styles.confirmText}>
                    {sub.status === "active" && sub.period_end
                      ? `You will keep service until ${fmtDate(sub.period_end)}. Cancellation stops future billing; nothing is refunded.`
                      : "Canceling now ends service immediately — you will never be charged."}
                    {sub.status === "trialing" && openDeposit && returnInfo && (
                      <>
                        {" "}
                        Your {dollars(openDeposit.price_cents)} box deposit comes back when you
                        return the box within {returnInfo.window_days} days of cancelling — we email
                        you the address and the steps.
                      </>
                    )}
                  </span>
                  <Button size="sm" disabled={busy} onClick={() => void handleCancel()}>
                    Cancel subscription
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => setCancelOpen(false)}
                  >
                    Keep my plan
                  </Button>
                </div>
              ) : (
                <div>
                  <Button variant="outline" disabled={busy} onClick={() => setCancelOpen(true)}>
                    Cancel…
                  </Button>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {canManage && (
        <section className={styles.card}>
          <h3 className={styles.cardTitle}>Invoices</h3>
          {invoices.length === 0 ? (
            <p className={styles.hint}>No invoices yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Amount</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{fmtDate(invoice.created_at)}</td>
                    <td>
                      {fmtDate(invoice.period_start)} – {fmtDate(invoice.period_end)}
                    </td>
                    <td>{dollars(invoice.total_cents)}</td>
                    <td data-status={invoice.status}>{invoiceStatusLabel(invoice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
};

// The 4b card-vault pattern (BillingStep): SetupIntent → Elements → confirmSetup →
// POST the pm token to the wallet route.
const AddCardFlow = ({
  storeId,
  onDone,
  onCancel,
}: {
  storeId: string;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "error"; message: string }
    | { kind: "ready"; publishableKey: string; clientSecret: string }
  >({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const config = await api.getBillingConfig();
        if (!config.publishable_key) {
          if (alive) setState({ kind: "error", message: "Billing isn't configured yet." });
          return;
        }
        const intent = await api.createBillingSetupIntent(storeId);
        if (alive) {
          setState({
            kind: "ready",
            publishableKey: config.publishable_key,
            clientSecret: intent.client_secret,
          });
        }
      } catch (e) {
        if (alive) setState({ kind: "error", message: errorText(e, "Couldn't start card setup.") });
      }
    })();
    return () => {
      alive = false;
    };
  }, [storeId]);

  const publishableKey = state.kind === "ready" ? state.publishableKey : null;
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  if (state.kind === "loading") return <div className={styles.hint}>Loading card setup…</div>;
  if (state.kind === "error") {
    return (
      <div className={styles.confirm}>
        <span className={styles.confirmText}>{state.message}</span>
        <Button variant="outline" size="sm" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }
  return (
    <Elements stripe={stripePromise} options={{ clientSecret: state.clientSecret }}>
      <AddCardForm storeId={storeId} onDone={onDone} onCancel={onCancel} />
    </Elements>
  );
};

const AddCardForm = ({
  storeId,
  onDone,
  onCancel,
}: {
  storeId: string;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const handleSave = async (): Promise<void> => {
    if (!stripe || !elements || saving) return;
    setSaving(true);
    setError(null);
    try {
      const result = await stripe.confirmSetup({ elements, redirect: "if_required" });
      if (result.error) {
        setError(result.error.message ?? "Your card couldn't be confirmed. Try again.");
        return;
      }
      const pm = result.setupIntent.payment_method;
      const pmId = typeof pm === "string" ? pm : (pm?.id ?? null);
      if (pmId === null) {
        setError("Card confirmation didn't return a payment method. Try again.");
        return;
      }
      await api.addPaymentMethod(storeId, { stripe_pm_id: pmId });
      onDone();
    } catch (e) {
      setError(errorText(e, "Couldn't save the card."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.addCard}>
      {error && (
        <div role="alert" className={styles.error}>
          {error}
        </div>
      )}
      <PaymentElement />
      <div className={styles.btnRow}>
        <Button disabled={saving || !stripe || !elements} onClick={() => void handleSave()}>
          {saving ? "Saving…" : "Save card"}
        </Button>
        <Button variant="outline" disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
};
